import { AuthFailureError, LogLevel } from '@magicdoorinc/auth';
import { createErrorFromResponse, getContentFromResponse } from '@magicdoorinc/errors';
import { toast } from '@magicdoorinc/ui';
import { auth, redirectToAuth } from '~/config/auth';
import { parseErrorMessage } from '~/errors/errorCodeMap';

const maskToken = (token?: string | null): string | undefined => (token ? `***${token.slice(-6)}` : undefined);

export abstract class BaseRestRepository {
  protected async _fetch(url: string, init: RequestInit, showErrorMessage = true) {
    return this.handleResponse(await fetch(url, init), url, showErrorMessage);
  }

  private async handleResponse(response: Response, url: string, showErrorMessage: boolean): Promise<Response> {
    if (response.ok) {
      return response;
    }
    const error = createErrorFromResponse(response.status, await getContentFromResponse(response));

    if (showErrorMessage) toast.error(parseErrorMessage(error));
    throw error;
  }

  protected async fetchWithAuth(url: string, options: RequestInit = {}, showErrorMessage = true): Promise<Response> {
    const init = await this.getRequestInit(options);
    const response = await this.authenticatedFetch(url, init);
    return this.handleResponse(response, url, showErrorMessage);
  }

  protected async authenticatedFetch(url: string, init: RequestInit): Promise<Response> {
    try {
      return await auth.authenticatedFetch(url, init);
    } catch (e) {
      if (e instanceof AuthFailureError) {
        auth.logger.log(LogLevel.INFO, 'unauthorized_error', {
          data: {
            url,
            access_token: maskToken(auth.storage.getTokenStorage().access_token),
          },
        });
        redirectToAuth(undefined, { logReason: `api_401:${url}` });
      }
      throw e;
    }
  }

  protected async fetchWithoutAuth(url: string, options: RequestInit = {}, showErrorMessage = true): Promise<Response> {
    const init = await this.getRequestInit(options);
    return this._fetch(url, init, showErrorMessage);
  }

  protected async getJsonResponse(response: Response, swallow = false) {
    if ([204, 205, 304].includes(response.status)) return undefined;

    const contentType = response.headers.get('Content-Type');
    if (contentType && contentType.includes('application/json')) {
      try {
        return await response.json();
      } catch (e: any) {
        if (response.status === 200) {
          console.error('Error parsing json:', e);
          if (swallow) {
            console.warn('Swallowing error');
            return {};
          }
          throw new Error('JSON parsing failed');
        }
      }
    }
    return undefined;
  }

  protected async getRequestInit(init: RequestInit = {}): Promise<RequestInit> {
    init.headers = init.headers || {};

    if (init.headers instanceof Headers) {
      init.headers = { ...init.headers };
    }

    const headers = init.headers as Record<string, string | undefined>;

    const contentType = headers['content-type'] || headers['Content-Type'];

    if (contentType === 'multipart/form-data') {
      // delete custom content-type, browser will add correct one
      delete headers['content-type'];
      delete headers['Content-Type'];
    } else if (contentType == null) {
      headers['content-type'] = 'application/json';
    }

    return init;
  }
}
